import React, { useEffect, useMemo, useState } from 'react';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { ArrowLeft, User, Mail, Crown, CreditCard, LogOut, Settings, Shield, Star, CheckCircle, Zap, FileText, Volume2, Brain, Loader2, Mic } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useUserStore } from '../../stores/userStore';
import { toast } from 'sonner';
import { usePublicPricing, formatPlanPrice } from '../../hooks/use-public-pricing';
import { useI18n, useLocaleFormat } from '../../i18n';
import { BillingPricingStatus } from './billing-pricing-status';
import { useSubscriptionStatus } from '../../hooks/use-subscription-status';
import { billingErrorKey } from '../../billing/billing-errors';
import type { BillingPlan, CheckoutProgress } from '../../billing/checkout-return';

interface ProfileViewProps {
  onClose: () => void;
  onShowSettings?: () => void;
  selectedPlan?: BillingPlan | null;
  billingProgress?: CheckoutProgress | 'cancelled' | 'portal' | null;
  onRetryBilling?: () => void;
}

export function ProfileView({ onClose, onShowSettings, selectedPlan, billingProgress, onRetryBilling }: ProfileViewProps) {
  const { t, tList, lang } = useI18n();
  const fmt = useLocaleFormat();
  const { getToken, userId } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { profile, limits, fetchProfile, fetchLimits, createCheckoutSession, createPortalSession } = useUserStore();
  const { pricing, loading: pricingLoading, error: pricingError, retry: retryPricing } = usePublicPricing();
  const billing = useSubscriptionStatus(billingProgress);
  const [supportRequired, setSupportRequired] = useState(false);

  const monthlyDisplay = useMemo(
    () =>
      pricing
        ? formatPlanPrice(pricing.monthlyAmount, pricing.currency)
        : '—',
    [pricing]
  );
  const annualDisplay = useMemo(
    () =>
      pricing
        ? formatPlanPrice(pricing.annualAmount, pricing.currency)
        : '—',
    [pricing]
  );
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  // Fetch user data on mount
  useEffect(() => {
    let cancelled = false;
    const current = () => !cancelled;
    const loadData = async () => {
      const token = await getToken();
      if (token && current()) {
        fetchProfile(token, current);
        fetchLimits(token, current);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [userId, getToken, fetchProfile, fetchLimits]);

  // Handle subscription upgrade
  const handleUpgrade = async (planType: 'monthly' | 'annual') => {
    if (!pricing || pricingLoading || !billing.status || isLoadingCheckout) return;
    const token = await getToken();
    if (!token) return;

    setIsLoadingCheckout(true);
    try {
      const url = await createCheckoutSession(planType, token, lang);
      window.location.href = url;
    } catch (error) {
      toast.error(t(billingErrorKey(error, 'profile.toast.checkoutFailed')));
      await Promise.all([fetchProfile(token), fetchLimits(token)]);
      billing.retry();
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  // Handle manage subscription
  const handleManageSubscription = async () => {
    const token = await getToken();
    if (!token) return;

    setIsLoadingPortal(true);
    try {
      const url = await createPortalSession(token, lang);
      window.location.href = url;
    } catch (error) {
      const message = billingErrorKey(error, 'profile.toast.portalFailed');
      if (message === 'billing.supportRequired') setSupportRequired(true);
      toast.error(t(message));
    } finally {
      setIsLoadingPortal(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    await signOut();
    toast.success(t('profile.toast.signedOut'));
  };

  const isPro = ['pro', 'paid', 'premium'].includes(billing.status?.tier ?? profile?.subscription_tier ?? 'free');
  const awaitingPayment = !!billingProgress && ['processing', 'pending', 'unavailable', 'open', 'needs_support'].includes(billingProgress);
  const purchaseDisabled = isLoadingCheckout || !pricing || pricingLoading || !billing.status || billing.status.hasActiveSubscription || awaitingPayment;

  return (
    <div className="flex h-full w-full min-h-[100%] flex-col bg-gradient-to-br from-white to-gray-50 overflow-auto">
      {/* Header */}
      <div className="border-b border-white/20 bg-white/60 backdrop-blur-xl px-4 sm:px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("common.back")}
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4 text-[#434343] rtl-flip" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-display text-[#434343]">{t('profile.title')}</h1>
        </div>
      </div>
                  
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-2xl mx-auto space-y-6 pb-8">
          {billingProgress && (
            <div className="rounded-xl border border-[#7367FF]/20 bg-[#7367FF]/5 p-4 text-sm leading-relaxed text-[#434343]" role="status" aria-live="polite">
              <p>{t(`billing.return.${billingProgress}`)}</p>
              {['pending', 'unavailable', 'open'].includes(billingProgress) && <Button onClick={onRetryBilling} variant="outline" className="mt-3">{t('billing.checkAgain')}</Button>}
              {['pending', 'unavailable', 'needs_support'].includes(billingProgress) && <a href="mailto:support@sourcity.ai" className="ms-3 inline-block py-2 text-[#7367FF] underline">{t('billing.contactSupport')}</a>}
            </div>
          )}
          {/* User Info Card */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center">
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="h-8 w-8 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#434343]">
                  {user?.fullName || user?.firstName || t('profile.defaultUser')}
                </h2>
                <p className="text-sm text-[#434343]/60 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {user?.primaryEmailAddress?.emailAddress}
                </p>
              </div>
            </div>

            {/* Subscription Badge */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
              isPro 
                ? 'bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white'
                : 'bg-[#434343]/10 text-[#434343]'
            }`}>
              {isPro ? <Crown className="h-4 w-4" /> : <User className="h-4 w-4" />}
              <span className="text-sm font-medium">
                {isPro ? t('common.proPlan') : t('common.freePlan')}
              </span>
            </div>
          </div>

          {/* Usage Stats */}
          {limits && limits.pdf && limits.tts && limits.explain && (
            <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-[#7367FF]" />
                {t('profile.usage.title')}
              </h3>
              
              <div className="space-y-4">
                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-[#434343] flex items-center gap-1">
                      <FileText className="h-4 w-4 text-[#7367FF]" />
                      {t('profile.usage.documents', { max: limits.pdfSize?.max ?? 10 })}
                    </span>
                    <span className="text-sm text-[#434343]/60">
                      {limits.pdf.unlimited ? (
                        <span className="text-[#3CFF9B]">{t('profile.usage.planAllowance')}</span>
                      ) : (
                        `${limits.pdf.current ?? 0} / ${limits.pdf.max ?? 2}`
                      )}
                    </span>
                  </div>
                  {!limits.pdf.unlimited && (
                    <Progress 
                      value={((limits.pdf.current ?? 0) / (limits.pdf.max || 1)) * 100} 
                      className="h-2"
                    />
                  )}
                </div>

                {/* Generated book audio allowance */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-[#434343] flex items-center gap-1">
                      <Volume2 className="h-4 w-4 text-[#7367FF]" />
                      {t('profile.usage.bookVoice')}
                    </span>
                    <span className="text-sm text-[#434343]/60">
                      {t('profile.usage.bookVoiceValue', {
                        pages: limits.bookAudio?.maxPages ?? (isPro ? 100 : 2),
                        words: fmt.number(limits.bookAudio?.maxWords ?? (isPro ? 10000 : 500)),
                      })}
                    </span>
                  </div>
                  <p className="text-xs text-[#434343]/50">
                    {t('profile.usage.bookVoiceNote')}
                  </p>
                </div>

                {/* Explanations */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-[#434343] flex items-center gap-1">
                      <Brain className="h-4 w-4 text-[#7367FF]" />
                      {t('profile.usage.explain')}
                    </span>
                    <span className="text-sm text-[#434343]/60">
                      {limits.explain.unlimited ? (
                        <span className="text-[#3CFF9B]">{t('profile.usage.includedSeparately')}</span>
                      ) : (
                        `${limits.explain.current ?? 0} / ${limits.explain.max ?? 2}`
                      )}
                    </span>
                  </div>
                  {!limits.explain.unlimited && (
                    <Progress 
                      value={((limits.explain.current ?? 0) / (limits.explain.max || 1)) * 100} 
                      className="h-2"
                    />
                  )}
                </div>

                {/* Realtime Voice */}
                {limits.realtime && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#434343] flex items-center gap-1">
                        <Mic className="h-4 w-4 text-[#7367FF]" />
                        {t('profile.usage.voice')}
                      </span>
                      <span className="text-sm text-[#434343]/60">
                        {limits.realtime.unlimited ? (
                          <span className="text-[#3CFF9B]">{t('profile.usage.proAccess')}</span>
                        ) : (
                          `${limits.realtime.current ?? 0} / ${limits.realtime.max ?? 60}`
                        )}
                      </span>
                    </div>
                    {!limits.realtime.unlimited && (
                      <Progress 
                        value={((limits.realtime.current ?? 0) / (limits.realtime.max || 1)) * 100} 
                        className="h-2"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Subscription Plans */}
          {!isPro && (
            <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
                <Star className="h-5 w-5 text-[#7367FF]" />
                {t('profile.upgrade.title')}
              </h3>

              <BillingPricingStatus loading={pricingLoading} error={pricingError} retry={retryPricing} />
              {selectedPlan && <p className="mb-4 text-sm text-[#7367FF]">{t(selectedPlan === 'annual' ? 'billing.selectedAnnual' : 'billing.selectedMonthly')}</p>}
              {!billing.status && <div role="status" className="mb-4 text-sm text-slate-600">{t(billing.error ? 'billing.statusUnavailable' : 'billing.statusLoading')}{billing.error && <Button variant="outline" onClick={billing.retry} className="ms-2">{t('billing.retry')}</Button>}</div>}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Monthly Plan */}
                <div className="border border-[#7367FF]/30 rounded-xl p-4 bg-[#7367FF]/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#434343]">{t('profile.upgrade.monthlyName')}</span>
                    <span className="text-2xl font-bold text-[#7367FF]" dir="ltr">{monthlyDisplay}</span>
                  </div>
                  <p className="text-sm text-[#434343]/60 mb-4">{t('profile.upgrade.perMonth')}</p>
                  <ul className="space-y-2 mb-4">
                    {tList('profile.upgrade.monthlyFeatures').map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-[#434343]">
                        <CheckCircle className="h-4 w-4 shrink-0 text-[#3CFF9B]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleUpgrade('monthly')}
                    disabled={purchaseDisabled}
                    className="w-full bg-[#7367FF] hover:bg-[#5f56e6] text-white"
                  >
                    {isLoadingCheckout ? (
                      <Loader2 className="h-4 w-4 animate-spin me-2" />
                    ) : null}
                    {t('profile.upgrade.subscribeMonthly')}
                  </Button>
                </div>

                {/* Annual Plan */}
                <div className="border-2 border-[#7367FF] rounded-xl p-4 bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 relative">
                    <div className="absolute -top-3 end-4 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white text-xs px-2 py-0.5 rounded-full">
                    {t('profile.upgrade.bestValue')}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#434343]">{t('profile.upgrade.annualName')}</span>
                    <span className="text-2xl font-bold text-[#7367FF]" dir="ltr">{annualDisplay}</span>
                  </div>
                  <p className="text-sm text-[#434343]/60 mb-4">{t('profile.upgrade.perYear')}</p>
                  <ul className="space-y-2 mb-4">
                    {tList('profile.upgrade.annualFeatures').map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-[#434343]">
                        <CheckCircle className="h-4 w-4 shrink-0 text-[#3CFF9B]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleUpgrade('annual')}
                    disabled={purchaseDisabled}
                    className="w-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white"
                  >
                    {isLoadingCheckout ? (
                      <Loader2 className="h-4 w-4 animate-spin me-2" />
                    ) : null}
                    {t('profile.upgrade.subscribeAnnually')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Billing history is also available for a linked, ended subscription. */}
          {(isPro || billing.status?.canManageBilling) && (
            <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[#7367FF]" />
                {t('profile.manage.title')}
              </h3>
              <p className="text-sm text-[#434343]/60 mb-4">
                {t('profile.manage.description')}
              </p>
              {!billing.status && !billing.error && <p role="status" className="text-sm text-slate-600">{t('billing.statusLoading')}</p>}
              {billing.error && <div role="status" className="text-sm text-slate-600"><p>{t('billing.statusUnavailable')}</p><Button variant="outline" onClick={billing.retry} className="mt-2">{t('billing.retry')}</Button></div>}
              {billing.status?.canManageBilling && !supportRequired && <Button
                onClick={handleManageSubscription}
                disabled={isLoadingPortal}
                variant="outline"
                className="border-[#7367FF] text-[#7367FF] hover:bg-[#7367FF]/10"
              >
                {isLoadingPortal ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Settings className="h-4 w-4 me-2" />}
                {t('billing.manageInvoices')}
              </Button>}
              {((billing.status && !billing.status.canManageBilling) || supportRequired) && <p className="mb-3 text-sm text-slate-600">{t('billing.supportRequired')}</p>}
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{t('billing.cancelThroughSupport')}</p>
              <a href="mailto:support@sourcity.ai" className="mt-2 inline-block py-2 text-sm text-[#7367FF] underline">{t('billing.contactSupport')}</a>
            </div>
          )}

          {/* Account Actions */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[#434343] mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#7367FF]" />
              {t('profile.account.title')}
            </h3>
            
            <div className="space-y-2">
              <Button
                variant="ghost"
                className="w-full justify-start text-[#434343] hover:bg-[#7367FF]/10"
                onClick={() => {
                  if (onShowSettings) {
                    onShowSettings();
                  } else {
                    toast.info(t('profile.toast.settingsSoon'));
                  }
                }}
              >
                <Settings className="h-4 w-4 me-2" />
                {t('profile.account.settings')}
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-red-500 hover:bg-red-50"
                onClick={handleSignOut}
              >
                <LogOut className="h-4 w-4 me-2" />
                {t('common.signOut')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
